import { reinitializeSwipers } from '../utilities/swiper-element'
import { reanimateImages } from '../utilities/utils'

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super()
    this.observer = undefined
  }

  connectedCallback() {
    if (window.Shopify && Shopify.designMode) {
      this.handleDesignMode()
    } else {
      this.initializeProducts()
    }
  }

  disconnectedCallback() {
    if (this._designModeListener) {
      document.removeEventListener('shopify:section:load', this._designModeListener)
    }
    this.observer?.unobserve(this)
  }

  handleDesignMode() {
    this._designModeListener = (event) => {
      const sectionId = event?.detail?.sectionId
      // Only initialize if the loaded section matches this element's section
      if (sectionId && this.dataset.sectionId && sectionId !== this.dataset.sectionId) {
        return
      }

      setTimeout(() => {
        if (this.isConnected) {
          this.loadProducts()
        }
      }, 1000)
    }

    document.addEventListener('shopify:section:load', this._designModeListener)

    // Also try loading immediately with delay in case section already loaded
    setTimeout(() => {
      if (this.isConnected && this.dataset.sectionId) {
        this.loadProducts()
      }
    }, 1000)
  }

  initializeProducts() {
    this.observer?.unobserve(this)

    this.observer = new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return
      observer.unobserve(this)
      this.loadProducts()
    }, { rootMargin: '0px 0px 200px 0px' })

    this.observer.observe(this)
  }

  getProductHandles() {
    if (typeof Storage === 'undefined') return []

    const expirationDays = window.recentlyViewedExpirationDays ?? 30
    const expirationMs = expirationDays * 24 * 60 * 60 * 1000
    const now = Date.now()

    let viewedProducts = localStorage.getItem('viewedProducts')
    viewedProducts = viewedProducts ? JSON.parse(viewedProducts) : []

    if (viewedProducts.length === 0) return []

    const normalizeEntry = (item) =>
      typeof item === 'string' ? { handle: item, viewedAt: now } : item
    const handles = viewedProducts
      .map(normalizeEntry)
      .filter((item) => (now - item.viewedAt) < expirationMs)
      .map((item) => item.handle)

    const currentProductHandle = this.dataset.currentProductHandle
    const filteredProducts = currentProductHandle
      ? handles.filter((handle) => handle !== currentProductHandle)
      : handles

    return filteredProducts
  }

  loadProducts() {
    const productHandles = this.getProductHandles()

    if (productHandles.length === 0) return

    const url = `${this.dataset.url}${encodeURIComponent(productHandles.join(','))}`

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div')
        html.innerHTML = text

        const sectionId = this.dataset.sectionId
        const section = html.querySelector(`#shopify-section-${sectionId}`)
        if (section) {
          const recentlyViewed = section.querySelector('recently-viewed-products')
          if (recentlyViewed?.innerHTML.trim().length) {
            this.innerHTML = recentlyViewed.innerHTML
            reanimateImages(this)

            if (this.querySelector('swiper-container')) {
              requestAnimationFrame(() => {
                reinitializeSwipers()
              })
            }
          }
        }
      })
      .catch((e) => {
        console.error('Error fetching recently viewed products:', e)
      })
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts)
